import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, ChevronLeft, Wrench, Package, HardHat, Truck, Zap, Paintbrush } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { COMPANY_INFO } from '@/lib/index';

const CATEGORIES = [
  { icon: HardHat, title: 'أعمال الهيكل والإنشاء', items: ['أعمال الخرسانة والحديد', 'البناء الهيكلي', 'الأساسات والخوازيق', 'الجدران الساندة', 'أعمال التكسية الحجرية'] },
  { icon: Wrench, title: 'الأعمال الميكانيكية', items: ['تمديدات التكييف المركزي', 'أنظمة الصرف الصحي', 'تمديدات المياه', 'غرف المحطات الميكانيكية', 'أنظمة الطاقة الشمسية'] },
  { icon: Zap, title: 'الأعمال الكهربائية', items: ['لوحات الكهرباء الرئيسية', 'الإنارة الداخلية والخارجية', 'أنظمة الأمن والمراقبة', 'أنظمة إطفاء الحريق', 'أنظمة الاتصالات'] },
  { icon: Paintbrush, title: 'التشطيبات والديكور', items: ['أعمال الجبس والدهان', 'الأرضيات والسيراميك', 'الأسقف المعلقة', 'الواجهات الزجاجية', 'الأبواب والنوافذ الألمنيوم'] },
  { icon: Package, title: 'مواد البناء والتوريد', items: ['الخرسانة الجاهزة', 'الحديد والصلب', 'مواد العزل', 'الرخام والحجر الطبيعي', 'مواد التشطيب الفاخرة'] },
  { icon: Truck, title: 'المعدات والخدمات اللوجستية', items: ['معدات الحفر الثقيلة', 'الرافعات والروافع', 'خدمات النقل والشحن', 'مولدات الكهرباء', 'أنظمة السقالات'] },
];

const REQUIREMENTS = [
  'سجل تجاري ساري المفعول',
  'شهادات الكفاءة والتصنيف',
  'سجل الأعمال المنجزة (portfolio)',
  'القدرة على العمل بالمدينة المنورة',
  'الالتزام بمعايير السلامة المهنية',
  'ملاءة مالية كافية لتنفيذ الأعمال',
];

export default function Contractors() {
  const [form, setForm] = useState({ name: '', company: '', phone: '', email: '', category: '', experience: '', message: '' });
  const [sent, setSent] = useState(false);
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 5000);
    setForm({ name: '', company: '', phone: '', email: '', category: '', experience: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'repeating-linear-gradient(45deg,transparent,transparent 40px,rgba(184,145,63,0.5) 40px,rgba(184,145,63,0.5) 41px)' }} />
        <div className="absolute inset-0">
          <img src="/images/luxury_hotel_building_construction_site__2.jpeg" alt="" className="w-full h-full object-cover opacity-20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/20 border border-gold/30 rounded-full text-gold text-sm mb-6">
              <HardHat size={14} /><span>للمقاولين والموردين</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-white mb-4" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              انضم إلى <span className="text-gold">شبكة شركائنا</span>
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto leading-relaxed">
              نبحث دائماً عن مقاولي الباطن والموردين المتميزين للانضمام إلى مشاريعنا الفندقية الفاخرة بالمدينة المنورة
            </p>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-navy mb-3" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              التخصصات <span className="text-gold">المطلوبة</span>
            </h2>
            <p className="text-muted-foreground">نحتاج خبرات في المجالات التالية لمشاريعنا الجارية والمستقبلية</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CATEGORIES.map(({ icon: Icon, title, items }, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.07 }} viewport={{ once: true }}
                className="group bg-surface border border-border rounded-2xl p-6 hover:border-navy/30 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-navy/8 rounded-xl flex items-center justify-center mb-4 group-hover:bg-navy transition-colors duration-300">
                  <Icon size={22} className="text-navy group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-navy font-bold mb-3">{title}</h3>
                <ul className="space-y-1.5">
                  {items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CheckCircle size={12} className="text-gold flex-shrink-0" />{item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16 bg-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-black text-navy mb-3" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              متطلبات <span className="text-gold">الانضمام</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {REQUIREMENTS.map((req, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: i * 0.06 }} viewport={{ once: true }}
                className="flex items-start gap-3 bg-white border border-border rounded-xl p-4">
                <CheckCircle size={18} className="text-gold flex-shrink-0 mt-0.5" />
                <span className="text-foreground text-sm font-medium">{req}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-black text-navy mb-3" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              سجّل <span className="text-gold">شركتك</span>
            </h2>
            <p className="text-muted-foreground">أرسل بياناتك وسيتواصل معك فريقنا لتقييم التعاون</p>
          </div>
          <div className="bg-white rounded-2xl shadow-xl border border-border p-8">
            <AnimatePresence>
              {sent && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="mb-5 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
                  ✅ تم استلام طلبك! سيتواصل معك فريقنا خلال 48 ساعة.
                </motion.div>
              )}
            </AnimatePresence>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">الاسم الكامل *</label>
                  <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="اسمك"
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">اسم الشركة *</label>
                  <input required value={form.company} onChange={e => set('company', e.target.value)} placeholder="شركتك"
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">الجوال *</label>
                  <input required type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} dir="ltr"
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">البريد الإلكتروني</label>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)} dir="ltr"
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">التخصص</label>
                  <select value={form.category} onChange={e => set('category', e.target.value)}
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all">
                    <option value="">اختر التخصص</option>
                    {CATEGORIES.map(c => <option key={c.title}>{c.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-1.5">سنوات الخبرة</label>
                  <select value={form.experience} onChange={e => set('experience', e.target.value)}
                    className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all">
                    <option value="">اختر</option>
                    <option>أقل من 5 سنوات</option><option>5 - 10 سنوات</option><option>10 - 20 سنة</option><option>أكثر من 20 سنة</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1.5">نبذة عن أعمالكم</label>
                <textarea rows={4} value={form.message} onChange={e => set('message', e.target.value)} placeholder="اذكر أبرز مشاريعكم المنجزة..."
                  className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:border-navy transition-all resize-none" />
              </div>
              <button type="submit"
                className="w-full py-4 bg-navy text-white rounded-xl font-bold text-base hover:bg-navy-light transition-all hover:shadow-lg flex items-center justify-center gap-2">
                إرسال طلب التسجيل <ChevronLeft size={18} />
              </button>
              <p className="text-center text-xs text-muted-foreground">
                أو تواصل مباشرة: <a href={`tel:${COMPANY_INFO.phone}`} className="text-gold font-semibold" dir="ltr">{COMPANY_INFO.phone}</a>
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
